import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Receipt, DollarSign, CheckCircle2, XCircle, Filter } from 'lucide-react';
import { fmtNum } from '@/utils/currency';

export default function Tax() {
  const { token } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('gst');
  const [loading, setLoading] = useState(false);
  
  // Tax Settings
  const [taxSettings, setTaxSettings] = useState({
    gst_enabled: false,
    gst_percent: 0,
    bpt_enabled: false,
    bpt_percent: 0
  });
  
  // GST State
  const [gstTransactions, setGstTransactions] = useState([]);
  const [gstSummary, setGstSummary] = useState({ total_gst: 0, total_paid: 0, total_unpaid: 0 });
  const [selectedGstIds, setSelectedGstIds] = useState([]);
  const [gstFilter, setGstFilter] = useState('all');
  const [gstPeriod, setGstPeriod] = useState('monthly');
  
  // BPT State
  const [bptCalculation, setBptCalculation] = useState({
    bpt_enabled: false,
    bpt_percent: 0,
    net_income: 0,
    bpt_amount: 0
  });
  const [bptRecords, setBptRecords] = useState([]);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentType, setPaymentType] = useState(''); // 'gst' or 'bpt'

  useEffect(() => {
    fetchTaxSettings();
    fetchGstTransactions();
    fetchBptCalculation();
    fetchBptRecords();
  }, []);

  const fetchTaxSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/tax`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaxSettings(response.data);
    } catch (error) {
      console.error('Failed to load tax settings');
    }
  };

  const updateTaxSettings = async (updates) => {
    setLoading(true);
    try {
      await axios.put(`${API}/settings/tax`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaxSettings({ ...taxSettings, ...updates });
      toast.success('Tax settings updated');
      
      // Refresh data
      if (updates.gst_enabled !== undefined || updates.gst_percent !== undefined) {
        fetchGstTransactions();
      }
      if (updates.bpt_enabled !== undefined || updates.bpt_percent !== undefined) {
        fetchBptCalculation();
      }
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchGstTransactions = async () => {
    try {
      const params = new URLSearchParams();
      if (gstFilter !== 'all') params.append('status', gstFilter);
      params.append('period', gstPeriod);
      
      const response = await axios.get(`${API}/tax/gst/transactions?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGstTransactions(response.data.transactions);
      setGstSummary(response.data.summary);
    } catch (error) {
      console.error('Failed to load GST transactions');
    }
  };

  const fetchBptCalculation = async () => {
    try {
      const response = await axios.get(`${API}/tax/bpt/calculation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBptCalculation(response.data);
    } catch (error) {
      console.error('Failed to load BPT calculation');
    }
  };

  const fetchBptRecords = async () => {
    try {
      const response = await axios.get(`${API}/tax/bpt/records`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBptRecords(response.data.records);
    } catch (error) {
      console.error('Failed to load BPT records');
    }
  };

  useEffect(() => {
    fetchGstTransactions();
  }, [gstFilter, gstPeriod]);

  const handleGstToggle = (transactionId) => {
    setSelectedGstIds(prev => 
      prev.includes(transactionId)
        ? prev.filter(id => id !== transactionId)
        : [...prev, transactionId]
    );
  };

  const handlePayGst = async (ids) => {
    if (ids.length === 0) {
      toast.error('Please select transactions to pay');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/tax/gst/pay`, ids, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setSelectedGstIds([]);
      fetchGstTransactions();
    } catch (error) {
      toast.error('Failed to process GST payment');
    } finally {
      setLoading(false);
    }
  };

  const handlePayBpt = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/tax/bpt/pay`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchBptCalculation();
      fetchBptRecords();
      setShowPaymentDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process BPT payment');
    } finally {
      setLoading(false);
    }
  };

  const selectedGstTotal = gstTransactions
    .filter(t => selectedGstIds.includes(t.id) && !t.is_paid)
    .reduce((sum, t) => sum + t.tax_amount, 0);

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Receipt className="w-8 h-8" />
            Tax Management
          </h1>
          <p className="text-slate-600 mt-1">Manage GST and BPT taxes</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="gst">GST (Goods & Services Tax)</TabsTrigger>
            <TabsTrigger value="bpt">BPT (Business Profit Tax)</TabsTrigger>
          </TabsList>

          {/* GST TAB */}
          <TabsContent value="gst" className="space-y-6">
            {/* GST Settings */}
            <Card className="p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">GST Settings</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label>Enable GST</Label>
                    <p className="text-sm text-slate-500">Activate GST on taxable products</p>
                  </div>
                  <Switch
                    checked={taxSettings.gst_enabled}
                    onCheckedChange={(checked) => updateTaxSettings({ gst_enabled: checked })}
                  />
                </div>

                <div>
                  <Label>GST Percentage (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={taxSettings.gst_percent}
                    onChange={(e) => setTaxSettings({ ...taxSettings, gst_percent: parseFloat(e.target.value) || 0 })}
                    onBlur={() => updateTaxSettings({ gst_percent: taxSettings.gst_percent })}
                    className="mt-2"
                  />
                </div>

                <div className="p-4 bg-emerald-50 rounded-lg">
                  <Label className="text-emerald-700">Total GST Collected</Label>
                  <p className="text-2xl font-bold text-emerald-900 mt-1">
                    ${fmtNum(gstSummary.total_gst)}
                  </p>
                  <div className="flex gap-4 mt-2 text-sm">
                    <span className="text-green-600">Paid: ${fmtNum(gstSummary.total_paid)}</span>
                    <span className="text-orange-600">Unpaid: ${fmtNum(gstSummary.total_unpaid)}</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* GST Filters */}
            <Card className="p-4">
              <div className="flex gap-4 items-center">
                <Filter className="w-5 h-5 text-slate-600" />
                <Select value={gstFilter} onValueChange={setGstFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={gstPeriod} onValueChange={setGstPeriod}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>

                {selectedGstIds.length > 0 && (
                  <div className="ml-auto flex items-center gap-4">
                    <span className="font-semibold">
                      Selected: ${fmtNum(selectedGstTotal)}
                    </span>
                    <Button
                      onClick={() => handlePayGst(selectedGstIds)}
                      disabled={loading}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <DollarSign className="w-4 h-4 mr-2" />
                      Pay Selected
                    </Button>
                  </div>
                )}
              </div>
            </Card>

            {/* GST Transactions Table */}
            <Card className="p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">GST Transactions</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="p-3 text-left">
                        <Checkbox
                          checked={selectedGstIds.length === gstTransactions.filter(t => !t.is_paid).length && gstTransactions.filter(t => !t.is_paid).length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedGstIds(gstTransactions.filter(t => !t.is_paid).map(t => t.id));
                            } else {
                              setSelectedGstIds([]);
                            }
                          }}
                        />
                      </th>
                      <th className="p-3 text-left">Product</th>
                      <th className="p-3 text-left">Reference</th>
                      <th className="p-3 text-right">Price</th>
                      <th className="p-3 text-right">Tax Amount</th>
                      <th className="p-3 text-center">Status</th>
                      <th className="p-3 text-left">Date</th>
                      <th className="p-3 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gstTransactions.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="p-8 text-center text-slate-500">
                          No GST transactions found
                        </td>
                      </tr>
                    ) : (
                      gstTransactions.map((trans) => (
                        <tr key={trans.id} className="border-t hover:bg-slate-50">
                          <td className="p-3">
                            {!trans.is_paid && (
                              <Checkbox
                                checked={selectedGstIds.includes(trans.id)}
                                onCheckedChange={() => handleGstToggle(trans.id)}
                              />
                            )}
                          </td>
                          <td className="p-3">{trans.product_name}</td>
                          <td className="p-3">
                            <span className="text-xs bg-slate-100 px-2 py-1 rounded">
                              {trans.reference_type.toUpperCase()}
                            </span>
                            <span className="ml-2 text-sm">{trans.reference_id}</span>
                          </td>
                          <td className="p-3 text-right">${fmtNum(trans.price)}</td>
                          <td className="p-3 text-right font-semibold">${fmtNum(trans.tax_amount)}</td>
                          <td className="p-3 text-center">
                            {trans.is_paid ? (
                              <span className="inline-flex items-center gap-1 text-green-600 bg-green-50 px-2 py-1 rounded text-sm">
                                <CheckCircle2 className="w-4 h-4" />
                                Paid
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-orange-600 bg-orange-50 px-2 py-1 rounded text-sm">
                                <XCircle className="w-4 h-4" />
                                Unpaid
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-sm">
                            {new Date(trans.date).toLocaleDateString()}
                          </td>
                          <td className="p-3 text-center">
                            {!trans.is_paid && (
                              <Button
                                size="sm"
                                onClick={() => handlePayGst([trans.id])}
                                disabled={loading}
                                variant="outline"
                              >
                                Pay
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* BPT TAB */}
          <TabsContent value="bpt" className="space-y-6">
            {/* BPT Settings */}
            <Card className="p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">BPT Settings</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label>Enable BPT</Label>
                    <p className="text-sm text-slate-500">Activate BPT calculation</p>
                  </div>
                  <Switch
                    checked={taxSettings.bpt_enabled}
                    onCheckedChange={(checked) => updateTaxSettings({ bpt_enabled: checked })}
                  />
                </div>

                <div>
                  <Label>BPT Percentage (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={taxSettings.bpt_percent}
                    onChange={(e) => setTaxSettings({ ...taxSettings, bpt_percent: parseFloat(e.target.value) || 0 })}
                    onBlur={() => updateTaxSettings({ bpt_percent: taxSettings.bpt_percent })}
                    className="mt-2"
                  />
                </div>

                <div className="p-4 bg-blue-50 rounded-lg">
                  <Label className="text-blue-700">Total BPT Calculated</Label>
                  <p className="text-2xl font-bold text-blue-900 mt-1">
                    ${fmtNum(bptCalculation.bpt_amount)}
                  </p>
                  <p className="text-sm text-blue-600 mt-1">
                    Based on Net Income: ${fmtNum(bptCalculation.net_income)}
                  </p>
                </div>
              </div>
            </Card>

            {/* BPT Calculation Details */}
            <Card className="p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">BPT Calculation</h2>
              <div className="space-y-4">
                <div className="flex justify-between p-4 bg-slate-50 rounded">
                  <span className="font-medium">Net Income (USD)</span>
                  <span className="font-bold text-lg">${fmtNum(bptCalculation.net_income)}</span>
                </div>
                <div className="flex justify-between p-4 bg-slate-50 rounded">
                  <span className="font-medium">BPT Rate</span>
                  <span className="font-bold text-lg">{bptCalculation.bpt_percent}%</span>
                </div>
                <div className="flex justify-between p-4 bg-blue-50 rounded border-2 border-blue-200">
                  <span className="font-bold text-blue-900">BPT Amount</span>
                  <span className="font-bold text-2xl text-blue-900">${fmtNum(bptCalculation.bpt_amount)}</span>
                </div>
                
                {bptCalculation.bpt_enabled && bptCalculation.bpt_amount > 0 && (
                  <Button
                    onClick={() => {
                      setPaymentType('bpt');
                      setShowPaymentDialog(true);
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    <DollarSign className="w-5 h-5 mr-2" />
                    Pay BPT Tax
                  </Button>
                )}
              </div>
            </Card>

            {/* BPT Payment Records */}
            <Card className="p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">BPT Payment History</h2>
              <div className="space-y-3">
                {bptRecords.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No BPT payments recorded</p>
                ) : (
                  bptRecords.map((record) => (
                    <div key={record.id} className="flex justify-between items-center p-4 bg-slate-50 rounded">
                      <div>
                        <p className="font-medium">BPT Payment</p>
                        <p className="text-sm text-slate-500">
                          {new Date(record.date).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">${fmtNum(record.amount)}</p>
                        <p className="text-sm text-slate-500">
                          Base: ${fmtNum(record.base_amount)}
                        </p>
                      </div>
                      <span className="inline-flex items-center gap-1 text-green-600 bg-green-50 px-3 py-1 rounded">
                        <CheckCircle2 className="w-4 h-4" />
                        Paid
                      </span>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Payment Confirmation Dialog */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Payment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p>Are you sure you want to pay BPT tax?</p>
              <div className="p-4 bg-slate-50 rounded">
                <p className="font-medium">Amount to Pay:</p>
                <p className="text-2xl font-bold text-blue-900">
                  ${fmtNum(bptCalculation.bpt_amount)}
                </p>
              </div>
              <p className="text-sm text-slate-600">
                This will create an expense entry under Category: Tax, Description: BPT
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={handlePayBpt}
                  disabled={loading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  Confirm Payment
                </Button>
                <Button
                  onClick={() => setShowPaymentDialog(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
